/* eslint-disable camelcase */
/**
 * @file EventReportFormView.jsx
 * @author Daniel Aguirre
 * @description Event Report Types View Page
 */

// Dependencies
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DatePicker, Input, Form } from 'antd';

// Helpers
import {
  getStorageItem,
  setStorageItem
} from '@nephro/commons/helpers/storage-helper';
import { formatModalDate, getFormItems } from 'pages/PatientEvents/helper';
import { disabledFutureDates } from 'commons/helpers/utils';

// Components
import ConfirmModal from 'components/ConfirmModal';
import FormGenerator from 'components/FormGenerator';
import ButtonOutlined from 'components/ButtonOutlined';
import ButtonFilled from 'components/ButtonFilled';

function EventReportFormView({
  patientEventActions,
  patientEventType,
  patientActions,
  actions
}) {
  const { updatePatient } = patientActions;
  const { setLoading } = actions;
  const { createPatientEvent } = patientEventActions;

  const formatDate = (date) => moment(date).format('YYYY-MM-DD');
  const formatTime = (date) => moment(date || moment()).format('HH:mm:ss');

  const history = useHistory();
  const [form] = Form.useForm();
  const [patientData, setPatientData] = useState(null);
  const [visibleConfirm, setVisibleConfirm] = useState(false);
  const [newEvent, setNewEvent] = useState(null);
  const [newPatientData, setNewPatientData] = useState(null);

  const { t: translate } = useTranslation();

  const onGoBack = () => {
    setVisibleConfirm(false);
    history.go(-2);
  };

  const onKeepOnlyNumbersAndChars = (string) =>
    string.replace(/[^0-9-,--]/g, '');

  const getUserData = async () => {
    const patientInfo = await getStorageItem('patientInfo');
    setPatientData(patientInfo);
    setLoading(false);
  };

  useEffect(() => {
    getUserData();
    if (!patientEventType) {
      history.goBack();
    }
  }, [patientEventType]);

  const handleFinish = ({
    description,
    registerDate,
    end_date,
    phone_number
  }) => {
    const data = {
      description,
      event_type: patientEventType,
      patient: patientData,
      phone_number,
      register_date: `${formatDate(registerDate)}T${formatTime(registerDate)}`,
      end_date: `${formatDate(end_date)}T${formatTime(end_date)}`
    };

    if (patientEventType?.title === 'Cambio de Telefono de Contacto') {
      const dataPatient = {
        id: patientData.id_patient,
        ...patientData,
        contact_phone_number: phone_number
      };
      setNewPatientData(dataPatient);
    }
    setNewEvent(data);
    setVisibleConfirm(true);
  };

  // Get and set patient info after login (only patient users)
  const onGetPatientInfo = async (type, response) => {
    if (type === 'success') {
      await setStorageItem('patientInfo', response.data);
    }
  };

  const handleCreate = () => {
    if (patientEventType?.title === 'Cambio de Telefono de Contacto')
      updatePatient(newPatientData, onGetPatientInfo);
    createPatientEvent(newEvent, onGoBack);
  };

  // Validate end date
  const validateEndDate = (rule, value, callback) => {
    const { registerDate, end_date } = form.getFieldsValue();
    if (moment(end_date) < moment(registerDate))
      return callback('La fecha final no puede ser menor a la fecha inicial');
    return Promise.resolve();
  };

  const aditionalFormItem = () => {
    let objectItem = [];
    if (patientEventType?.title === 'Hospitalización') {
      objectItem = [
        {
          item: (
            <DatePicker
              disabledDate={disabledFutureDates}
              format={'DD/MM/YYYY'}
            />
          ),
          name: ['registerDate'],
          label: translate('Fecha de inicio'),
          rules: [{ required: true }]
        },
        {
          item: (
            <DatePicker
              disabledDate={disabledFutureDates}
              format={'DD/MM/YYYY'}
            />
          ),
          name: ['end_date'],
          label: translate('Fecha de fin'),
          rules: [
            { required: true },
            {
              validator: validateEndDate
            }
          ]
        }
      ];
    }

    if (patientEventType?.title === 'Cambio de Telefono de Contacto') {
      objectItem = [
        {
          item: <Input maxLength={100} />,
          name: ['phone_number'],
          label: translate('Teléfono(s) de contacto'),
          normalize: onKeepOnlyNumbersAndChars,
          rules: [{ required: true }],
          tooltip:
            'Puedes agregar varios números y separarlos por coma(,) y (-)',
          breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
        }
      ];
    }

    return objectItem;
  };

  const onGetFormItems = () => {
    const items = [...getFormItems(translate)];
    if (patientEventType?.title === 'Hospitalización') {
      items.forEach((itm, idx) => {
        if (itm.label === translate('patientEvents.date')) items.splice(idx, 1);
      });
    }

    return items;
  };
  return (
    <div className="p-5 text-baxterGray">
      <h1 className="mb-5" style={{ fontSize: 20 }}>
        {translate('patientEvents.report')}
        <span className="lowercase">
          {' '}
          {translate(`patientEvents.${patientEventType?.title}`)}
        </span>
      </h1>
      <section>
        <p className="mb-5" style={{ fontSize: 18 }}>
          {translate('patientEvents.formDescription')}
        </p>
        <FormGenerator
          formParam={form}
          initialValues={{}}
          onFinish={handleFinish}
          items={[...aditionalFormItem(), ...onGetFormItems()]}
          bottomOptions={[
            <div className="w-full pr-2" key="1">
              <ButtonOutlined
                type="cyan"
                textButton={translate('commons.cancel')}
                onActionClick={onGoBack}
              />
            </div>,
            <div className="w-full pl-2" key="2">
              <ButtonFilled
                type="cyan"
                htmlType="submit"
                textButton={translate('commons.confirm')}
              />
            </div>
          ]}
        />
      </section>
      <ConfirmModal
        visibleConfirm={visibleConfirm}
        onCancel={() => setVisibleConfirm(false)}
        mode={'confirm'}
        onSubmit={handleCreate}
        onSubmitText={'patientRecords.confirm'}
        contentComponent={
          <div className="bg-highLight flex flex-col justify-center items-center p-2 rounded-md mt-2">
            <h3 className="text-base text-secondary mb-0">
              {patientEventType?.title}
            </h3>
            <h3 className="text-base text-secondary mb-0 mt-2">
              <ul>
                <li>
                  {newEvent?.event_type?.title === 'Hospitalización'
                    ? `${translate('Fecha inicio')}`
                    : `${translate('patientEvents.date')}`}
                  {`: ${formatModalDate(newEvent?.register_date, translate)}`}
                </li>
                {newEvent?.event_type?.title === 'Hospitalización' && (
                  <li>
                    {`${translate('Fecha fin')}:
                  ${formatModalDate(newEvent?.end_date, translate)}`}
                  </li>
                )}
              </ul>
            </h3>
          </div>
        }
      />
    </div>
  );
}

/**
 * Component definition of proptypes.
 */
EventReportFormView.propTypes = {
  patientEventActions: PropTypes.shape({
    createPatientEvent: PropTypes.func.isRequired
  }).isRequired,
  patientEventType: PropTypes.object,
  patientActions: PropTypes.shape({
    updatePatient: PropTypes.func.isRequired
  }).isRequired,
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired
};
export default EventReportFormView;
